import React from "react";
import "./Process.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClipboardList,
  faPenToSquare,
  faRocket,
  faChartLine,
  faHandshake,
} from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";

const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.09 } },
};

const item = {
  hidden: { opacity: 0, y: 22 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1] } },
};

const steps = [
  {
    title: "Discovery & audit",
    description:
      "Review goals, channels, analytics, creative assets, and constraints. Outputs may include a findings deck and prioritized opportunity list.",
    icon: faClipboardList,
    bullets: ["Stakeholder intake", "Account & site access", "Baseline metrics"],
  },
  {
    title: "Strategy & scope",
    description:
      "Agree on channel mix, timelines, deliverables, and fees in a written proposal or SOW—with assumptions and exclusions spelled out.",
    icon: faPenToSquare,
    bullets: ["KPIs & reporting", "Budget pacing", "Legal / compliance flags"],
  },
  {
    title: "Build & launch",
    description:
      "Campaigns, content, tags, and landing updates go live with QA checklists and brand, legal, or product approvals when needed.",
    icon: faRocket,
    bullets: ["Creative handoffs", "Tracking verification", "Launch communications"],
  },
  {
    title: "Optimize & report",
    description:
      "Monitor performance, run structured tests where appropriate, and share readable reports tied to your goals—not vanity metrics alone.",
    icon: faChartLine,
    bullets: ["Weekly / biweekly updates", "Monthly reviews", "Change logs"],
  },
  {
    title: "Renew or transition",
    description:
      "Recap learnings, document accounts, and support an orderly handoff if you move work in-house or to another partner.",
    icon: faHandshake,
    bullets: ["Asset export", "Admin access", "Knowledge transfer"],
  },
];

const Process = () => {
  return (
    <section className="prc" aria-labelledby="process-heading">
      <div className="prc__wrap">
        <motion.div
          className="prc__head"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.65 }}
        >
          <p className="prc__eyebrow">How we deliver</p>
          <h2 id="process-heading" className="prc__title">
            A marketing process built for accountability
          </h2>
          <p className="prc__sub">
            Steps flex by engagement type. Rush launches may compress phases; enterprise work may add security or
            procurement reviews. Your timely feedback and accurate product info help us stay compliant and move faster.
          </p>
        </motion.div>

        <motion.ol
          className="prc__grid"
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.15 }}
        >
          {steps.map((s, i) => (
            <motion.li key={s.title} className="prc__card" variants={item}>
              <div className="prc__top">
                <span className="prc__num">{String(i + 1).padStart(2, "0")}</span>
                <div className="prc__icon" aria-hidden="true">
                  <FontAwesomeIcon icon={s.icon} />
                </div>
              </div>
              <h3 className="prc__h3">{s.title}</h3>
              <p className="prc__p">{s.description}</p>
              <ul className="prc__bullets">
                {s.bullets.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </motion.li>
          ))}
        </motion.ol>
      </div>
    </section>
  );
};

export default Process;
